import React, { type ReactNode, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { FocusIcon } from '@atlaskit/logo';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss 
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	getProductAndIDFromARI,
	isSearchResultMercuryFocusAreaPartial,
	isSearchResultMercuryFocusAreaStatusUpdatePartial,
	isSearchResultSpfAskPartial,
	type SearchMercuryPartial,
	type SearchResultMercuryFocusArea,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../../common/constants/products';
import { CalendarXSIcon } from '../../../../../../common/ui/calendar-xs-icon';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../controllers/store/bootstrap/accessors';
import { useSearchQuery, useSelectedProduct } from '../../../../../../controllers/store/search';

import { messages } from './messages';
import { getDisplayedAskStatus, getDisplayedFocusAreaStatus } from './utils/statusHealth';
import { getDisplayedAskTargetDate, getDisplayedFocusAreaTargetDate } from './utils/targetDate';

const getType = (partial: SearchMercuryPartial) => {
	return (
		<Box>
			<SearchResultType type={partial.type} />
		</Box>
	);
};

const getFocusAreaType = (partial: SearchResultMercuryFocusArea) => {
	return <Box>{partial.focusArea?.focusAreaType?.name}</Box>;
};

const getOwner = (partial: SearchResultMercuryFocusArea) => {
	return <Box>{partial.focusArea?.owner?.name}</Box>;
};

const getLastModified = (partial: SearchMercuryPartial) => {
	if (partial.lastModifiedDate) {
		return (
			<Box>
				<LastModified lastModified={partial.lastModifiedDate} />
			</Box>
		);
	}
	return null;
};

const statusAndTargetDateXcss = xcss({ display: 'flex', alignItems: 'center', gap: 'space.100' });
const targetDateForXcss = xcss({ font: token('font.body.small'), color: 'color.text.subtlest' });
const getStatusAndTargetDate = (
	displayedStatusAppearance: ThemeAppearance,
	displayedStatus?: string,
	displayedTargetDate?: string
) => {
	if (!displayedStatus) {
		return null;
	}

	return (
		<Box xcss={statusAndTargetDateXcss}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
			<Lozenge appearance={displayedStatusAppearance}>{displayedStatus}</Lozenge>
			{displayedTargetDate && (
				<>
					<Box xcss={targetDateForXcss}>
						<FormattedMessage {...messages.for} />
					</Box>
					<Lozenge>
						<Inline alignBlock="center" space="space.050">
							<CalendarXSIcon />
							{displayedTargetDate}
						</Inline>
					</Lozenge>
				</>
			)}
		</Box>
	);
};

interface MercurySearchResultProps {
	partial: SearchMercuryPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const MercurySearchResult = ({
	partial,
	position,
}: MercurySearchResultProps): React.JSX.Element => {
	const [{ cloudId }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [searchQuery] = useSearchQuery();
	const applicationMode = useApplicationMode();
	const intl = useIntl();

	const { id, url, type } = partial;

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(id);
		return {
			id,
			containerId: null,
			spaceId: null,
			contentType: type,
			entityType: type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [id, type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	let title: ReactNode = '';
	let description: ReactNode = '';
	let headerMetadataParts: any[] = [];

	if (isSearchResultMercuryFocusAreaPartial(partial)) {
		const { displayedStatus, displayedStatusAppearance } = getDisplayedFocusAreaStatus(partial);
		const displayedTargetDate = getDisplayedFocusAreaTargetDate(partial);
		title = enrichExcerpt(partial.title);
		description = getStatusAndTargetDate(displayedStatusAppearance, displayedStatus, displayedTargetDate ?? undefined);
		headerMetadataParts = [
			['type', getType(partial)],
			['focusAreaType', getFocusAreaType(partial)],
			partial.focusArea?.owner ? ['owner', getOwner(partial)] : null,
			['last-modified', getLastModified(partial)],
		];
	}

	if (isSearchResultMercuryFocusAreaStatusUpdatePartial(partial)) {
		title = intl.formatMessage(messages.statusUpdateTitle, {
			focusAreaName: partial.focusArea?.name,
		});
		description = getSummarizedAdfContent(partial.description, searchQuery);
		headerMetadataParts = [
			['type', getType(partial)],
			['last-modified', getLastModified(partial)],
		];
	}

	if (isSearchResultSpfAskPartial(partial) && fg('platform_passionfruit_search_onboarding_enabled')) {
		const { displayedStatus, displayedStatusAppearance } = getDisplayedAskStatus(partial);
		const displayedTargetDate = getDisplayedAskTargetDate(partial);
		title = enrichExcerpt(partial.title);
		description = getStatusAndTargetDate(displayedStatusAppearance, displayedStatus, displayedTargetDate ?? undefined);
		headerMetadataParts = [
			['type', getType(partial)],
			['last-modified', getLastModified(partial)],
		];
	}

	return (
		<SearchResult
			{...partial}
			product={ProductKeys.Focus}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={title}
			icon={<FocusIcon size="medium" shouldUseNewLogoDesign />}
			headerMetadataParts={headerMetadataParts}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={partial}
			appMode={applicationMode}
		/>
	);
};
